import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useGlobalStore, type GlobalState } from '@digiwedge/global-state';
import {
  getAdminJwt,
  getAdminToken,
  setAdminJwt,
  setAdminToken,
  getPricingCustomerId,
  setPricingCustomerId,
} from '../../utils/pricingContext.js';
import { decodeJwtPayload } from '../../utils/jwt.js';
import './AdminHomePage.css';

const ADMIN_SECTIONS = [
  {
    title: 'Commerce Control',
    summary: 'Pricing, contracts, catalog caching, and checkout routing.',
    tags: ['yolo-site-api', 'axiz', 'payments'],
    items: [
      {
        label: 'Pricing & Markups',
        description: 'GP profiles, contract mapping, and pricing simulator.',
        to: '/admin/pricing',
        status: 'live',
      },
      {
        label: 'Catalog Sync',
        description: 'Axiz product cache, freshness, and market filters.',
        status: 'plan',
      },
      {
        label: 'Orders & Fulfillment',
        description: 'Payment capture, order status, and ERP export.',
        to: '/admin/orders',
        status: 'live',
      },
      {
        label: 'Quote Pipeline',
        description: 'MSP quotes, approvals, and follow-up automation.',
        status: 'soon',
      },
    ],
  },
  {
    title: 'Services & Platforms',
    summary: 'Managed services, VoicePro, MSP AI, and partner integrations.',
    tags: ['msp-ai', 'voicepro', 'crm'],
    items: [
      {
        label: 'MSP AI Tenants',
        description: 'Tenant onboarding, access tiers, and diagnostics.',
        status: 'beta',
      },
      {
        label: 'VoicePro Operations',
        description: '3CX health, recordings, and integration registry.',
        status: 'dev',
      },
      {
        label: 'Security Services',
        description: 'Huntress + Acronis service health monitoring.',
        status: 'plan',
      },
      {
        label: 'CRM & Leads',
        description: 'Public lead intake, routing, and quality checks.',
        status: 'plan',
      },
    ],
  },
  {
    title: 'Configuration Hub',
    summary: 'Centralized config, secrets, and environment hygiene.',
    tags: ['infisical', 'kubernetes', 'alerts'],
    items: [
      {
        label: 'Secrets & Keys',
        description: 'Infisical sync status and key validation.',
        status: 'plan',
      },
      {
        label: 'Runtime Health',
        description: 'Service status, probes, and readiness history.',
        status: 'plan',
      },
      {
        label: 'Release Checklist',
        description: 'Deployment metadata and build info validation.',
        status: 'plan',
      },
      {
        label: 'Site Content',
        description: 'Hero, services, specials, and featured categories.',
        to: '/admin/content',
        status: 'live',
      },
    ],
  },
];

const STATUS_LABELS: Record<string, string> = {
  live: 'Live',
  beta: 'Beta',
  dev: 'Dev',
  plan: 'Planned',
  soon: 'Soon',
  audit: 'Audit',
};

const AdminHomePage: React.FC = () => {
  const [tokenInput, setTokenInput] = useState(getAdminToken() ?? '');
  const [savedToken, setSavedToken] = useState(getAdminToken() ?? '');
  const [jwtInput, setJwtInput] = useState(getAdminJwt() ?? '');
  const [savedJwt, setSavedJwt] = useState(getAdminJwt() ?? '');
  const [customerInput, setCustomerInput] = useState(getPricingCustomerId() ?? '');
  const storeToken = useGlobalStore((s: GlobalState) => s.token);

  const tokenStatus = useMemo(() => {
    if (storeToken?.trim()) {
      const payload = decodeJwtPayload(storeToken);
      const roles = payload?.roles?.join(', ');
      return roles ? `IDP session active · Roles: ${roles}` : 'IDP session active';
    }
    if (savedJwt.trim()) {
      const payload = decodeJwtPayload(savedJwt);
      const roles = payload?.roles?.join(', ');
      return roles ? `JWT active · Roles: ${roles}` : 'JWT active';
    }
    if (savedToken.trim()) {
      return 'Legacy admin token stored';
    }
    return 'No admin auth configured yet';
  }, [savedJwt, savedToken, storeToken]);

  const handleSaveToken = () => {
    setAdminToken(tokenInput);
    setSavedToken(tokenInput);
  };

  const handleSaveJwt = () => {
    setAdminJwt(jwtInput);
    setSavedJwt(jwtInput);
  };

  const handleSaveCustomer = () => {
    setPricingCustomerId(customerInput);
  };

  return (
    <div className="admin-home-page">
      <section className="page-hero admin-home-hero">
        <div className="container">
          <span className="badge badge-navy">Admin</span>
          <h1>Admin Command Deck</h1>
          <p>
            Orchestrate commerce, services, and platform configuration in one place. Track what is live,
            what is in flight, and what needs action.
          </p>
        </div>
      </section>

      <section className="section">
        <div className="container admin-home-grid">
          <div className="panel admin-access-panel">
            <h2>Access & Context</h2>
            <p className="muted">Keep your admin auth and pricing context on hand for quick actions.</p>
            <div className="admin-input-row">
              <input
                type="password"
                value={jwtInput}
                onChange={(event) => setJwtInput(event.target.value)}
                placeholder="Admin JWT (preferred)"
              />
              <button type="button" className="btn btn-primary" onClick={handleSaveJwt}>
                Save JWT
              </button>
            </div>
            <div className="admin-input-row">
              <input
                type="password"
                value={tokenInput}
                onChange={(event) => setTokenInput(event.target.value)}
                placeholder="Legacy admin token (fallback)"
              />
              <button type="button" className="btn btn-outline" onClick={handleSaveToken}>
                Save Token
              </button>
            </div>
            <div className="admin-input-row">
              <input
                type="text"
                value={customerInput}
                onChange={(event) => setCustomerInput(event.target.value)}
                placeholder="Pricing customer ID (optional)"
              />
              <button type="button" className="btn btn-outline" onClick={handleSaveCustomer}>
                Save Context
              </button>
            </div>
            <p className="admin-access-status">{tokenStatus}</p>
          </div>

          <div className="panel admin-quick-panel">
            <h2>Quick Actions</h2>
            <ul className="admin-quick-list">
              <li>
                <Link to="/admin/pricing" className="admin-quick-link">
                  Open Pricing Control Center
                </Link>
              </li>
              <li>
                <Link to="/admin/content" className="admin-quick-link">
                  Manage Site Content
                </Link>
              </li>
              <li>
                <Link to="/admin/orders" className="admin-quick-link">
                  View Orders Dashboard
                </Link>
              </li>
              <li>
                <a
                  href="/api/yolo/v1/health"
                  className="admin-quick-link"
                  target="_blank"
                  rel="noreferrer"
                >
                  View API Health Probe
                </a>
              </li>
              <li>
                <a
                  href="/api/yolo/v1/docs"
                  className="admin-quick-link"
                  target="_blank"
                  rel="noreferrer"
                >
                  Open Swagger Docs
                </a>
              </li>
            </ul>
          </div>
        </div>
      </section>

      <section className="section section-gray">
        <div className="container admin-service-grid">
          {ADMIN_SECTIONS.map((section) => (
            <article key={section.title} className="panel admin-service-card">
              <div className="admin-card-header">
                <div>
                  <h2>{section.title}</h2>
                  <p className="muted">{section.summary}</p>
                </div>
                <div className="admin-tag-row">
                  {section.tags.map((tag) => (
                    <span key={tag} className="admin-tag">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
              <ul className="admin-nav-list">
                {section.items.map((item) => {
                  const isDisabled = !item.to;
                  const statusLabel = STATUS_LABELS[item.status] ?? item.status;
                  return (
                    <li key={item.label} className={`admin-nav-item ${isDisabled ? 'is-disabled' : ''}`}>
                      <div>
                        {item.to ? (
                          <Link to={item.to} className="admin-nav-link">
                            {item.label}
                          </Link>
                        ) : (
                          <span className="admin-nav-link" aria-disabled="true">
                            {item.label}
                          </span>
                        )}
                        <span className="admin-nav-desc">{item.description}</span>
                      </div>
                      <span className={`badge status-${item.status}`}>{statusLabel}</span>
                    </li>
                  );
                })}
              </ul>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
};

export default AdminHomePage;
